import type { JSX } from "react";

import { NoAccessCard } from "@impero-com/common/components/noAccessCard";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FormLabel, TSTTable } from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

import type { LegacyControlProgramViewModelWithUserMap } from "api/legacy/model/legacyControlProgramViewModelWithUserMap";
import type { LegacyPermissionAndUserId } from "api/legacy/model/legacyPermissionAndUserId";

import * as styles from "./UsersPanel.css";

interface UsersPanelProps {
    model: LegacyControlProgramViewModelWithUserMap;
    userName: (userId: number | null | undefined) => string;
}

const columnHelper = createColumnHelper<LegacyPermissionAndUserId>();

export function UsersPanel({ model, userName }: UsersPanelProps): JSX.Element {
    const { t } = useTranslation();

    const permissionLabel = (permission: string): string => {
        switch (permission) {
            case "Admin":
                return t("AccessRights_Admin");
            case "Edit":
                return t("AccessRights_Edit");
            case "View":
                return t("AccessRights_View");
            default:
                return permission;
        }
    };

    const columns = [
        columnHelper.accessor("UserId", {
            header: t("AccessRights_UserColum"),
            cell: (info) => (
                <div>
                    <div className={styles.nameCell}>
                        {userName(info.getValue())}
                    </div>
                    <div className={styles.email}>
                        {model.Users[info.getValue()]?.Email ?? ""}
                    </div>
                </div>
            ),
        }),
        columnHelper.accessor("AccessControlPermission", {
            header: t("AccessRights_AccessRightsColumn"),
            cell: (info) => permissionLabel(info.getValue()),
        }),
    ];

    const table = useReactTable({
        data: model.PermissionAndUserIds,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: false,
        enableSorting: false,
    });

    if (model.PermissionAndUserIds.length === 0 && !model.ResponsibleUserId) {
        return <NoAccessCard />;
    }

    return (
        <div>
            {model.ResponsibleUserId ? (
                <div className={styles.responsibleBlock}>
                    <FormLabel>{t("AccessRights_Responsible")}</FormLabel>
                    <div>{userName(model.ResponsibleUserId)}</div>
                </div>
            ) : null}
            <FormLabel>{t("AccessRights_AccessManagement")}</FormLabel>
            <TSTTable
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
            />
        </div>
    );
}
